import React, { useState, useEffect } from 'react';
import { IconButton, InputAdornment, TextField, Tooltip, FormControlLabel, Switch, Alert } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { stylesheet } from 'typestyle';
import { percent } from 'csx';
import { ADALONG_COLORS } from '@src/styles';
import { Loader } from '@src/components/Loader';
import { CRMTemplateView } from 'common';
import { useAtomValue } from 'jotai';
import { setPopup, setCrmSelectedTemplate } from '@src/state/popupAtoms';
import { EPopup } from '@src/types/common';
import { crmSelectedTemplateAtom } from '@src/state/popupAtoms';
import { DmConversation } from '@adalong/schemas';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { extractCreatorHandle, processTemplateContent } from '@src/utils/crmTemplates';

export function MessageWriter(props: {
  text: string;
  isLoading: boolean;
  onChange: (text: string) => void;
  onSend: (text: string) => void;
  conversation?: DmConversation;
  creatorName?: string;
}) {
  const crmSelectedTemplate = useAtomValue(crmSelectedTemplateAtom);
  const { data: userConfig } = useQueryUserConfig();
  const [selectedTemplate, setSelectedTemplate] = useState<CRMTemplateView | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateWarnings, setTemplateWarnings] = useState<string[]>([]);
  const [hasUsedTemplateSelector, setHasUsedTemplateSelector] = useState(
    localStorage.getItem('hasUsedTemplateSelector') === 'true'
  );
  const [showPulse, setShowPulse] = useState(false);
  const [enterToSend, setEnterToSend] = useState(
    localStorage.getItem('enterToSend') !== 'false' // Default to true
  );

  // Pulse animation effect for template selector
  useEffect(() => {
    if (!hasUsedTemplateSelector) {
      const interval = setInterval(() => {
        setShowPulse(true);
        setTimeout(() => setShowPulse(false), 1500); 
      }, 5000); 

      return () => clearInterval(interval);
    }
  }, [hasUsedTemplateSelector]);

  // Listen for template selection from atom
  useEffect(() => {
    if (crmSelectedTemplate) {
      // Process template content to replace variables
      const { processedContent, warnings } = processTemplateContent({
        content: crmSelectedTemplate.content,
        currentTemplate: crmSelectedTemplate,
        creatorHandle: extractCreatorHandle(props.creatorName, props.conversation),
        termsUrl: crmSelectedTemplate?.terms || userConfig?.user?.group?.terms_url
      });
      props.onChange(processedContent);
      setTemplateWarnings(warnings);
      setSelectedTemplate(crmSelectedTemplate);
      setSelectedTemplateId(crmSelectedTemplate._id); // Track the ID for template selector
      // Clear the atom state after handling it
      setCrmSelectedTemplate(null);
    }
  }, [crmSelectedTemplate, props]);

  const handleToggleEnterToSend = (checked: boolean) => {
    setEnterToSend(checked);
    localStorage.setItem('enterToSend', checked.toString());
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (enterToSend && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (props.text.trim()) {
        handleSendMessage();
      }
    }
  };

  const handleOpenTemplateSelector = () => {
    if (!hasUsedTemplateSelector) {
      setHasUsedTemplateSelector(true);
      localStorage.setItem('hasUsedTemplateSelector', 'true');
    }
    setPopup(EPopup.templateSelector, { 
      filterType: 'DM',
      selectedTemplateId: selectedTemplateId || undefined // Pass the current selected template ID
    });
  };

  const handleSendMessage = () => {
    props.onSend(props.text);
    setSelectedTemplate(null); // Clear template selection after sending
    setSelectedTemplateId(null); // Clear template ID after sending
  };

  const handleTextChange = (text: string) => {
    props.onChange(text);
    // Clear selected template if text is manually changed
    if (selectedTemplate) {
      setSelectedTemplate(null);
      setSelectedTemplateId(null);
    }
  };

  return (
    <>
      <div className={sheet.container}>
        <TextField
          id='outlined-start-adornment'
          multiline
          sx={{
            m: 1,
            width: percent(100),
            margin: 0,
          }}
          placeholder='Write a message'
          value={props.text}
          onChange={(e) => handleTextChange(e.target.value)}
          onKeyPress={handleKeyPress}
          className={sheet.textField}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Tooltip 
                  title="Select a template to quickly fill your message" 
                  placement="top"
                  arrow
                >
                  <IconButton
                    sx={{ 
                      color: ADALONG_COLORS.SMOOTH_WHITE, 
                      marginRight: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      ...(showPulse && {
                        animation: 'pulse 1.5s ease-in-out',
                        '@keyframes pulse': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.4)',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          },
                          '50%': {
                            boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.1)',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          },
                        },
                      }),
                    }}
                    aria-label='select template'
                    onClick={handleOpenTemplateSelector}
                  >
                    <ListAltIcon />
                  </IconButton>
                </Tooltip>
                {!props.isLoading ? (
                  <IconButton
                    sx={{ color: ADALONG_COLORS.SMOOTH_WHITE }}
                    aria-label='send message'
                    onClick={handleSendMessage}
                  >
                    <SendIcon />
                  </IconButton>
                ) : (
                  <Loader />
                )}
              </InputAdornment>
            ),
          }}
          variant='outlined'
        />
        
        {/* Enter to send toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={enterToSend}
              onChange={(e) => handleToggleEnterToSend(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: ADALONG_COLORS.SMOOTH_WHITE,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: ADALONG_COLORS.LIGHT_GRAY,
                },
              }}
            />
          }
          label={
            <span style={{ fontSize: 12, color: ADALONG_COLORS.LIGHT_GRAY }}>
              Press Enter to send {!enterToSend && '(Shift+Enter for new line)'}
            </span>
          }
          sx={{ margin: '8px 0 0 4px' }}
        />
        
        {selectedTemplate ? (
          <div style={{
            fontSize: 12,
            color: ADALONG_COLORS.LIGHT_GRAY,
            marginTop: 8,
            padding: '8px 12px',
            backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
            borderRadius: 4,
            border: '1px solid #444',
          }}>
            Template selected: <span style={{ color: ADALONG_COLORS.SMOOTH_WHITE, fontWeight: 500 }}>{selectedTemplate.name}</span>
            
            {templateWarnings.length > 0 && (
              <Alert 
                severity="warning" 
                variant="outlined" 
                sx={{ 
                  marginTop: 1,
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  '& .MuiAlert-message': { 
                    fontSize: '11px',
                    color: '#ff9800'
                  },
                  '& .MuiAlert-icon': {
                    color: '#ff9800'
                  }
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>
                  Template warnings:
                </div>
                {templateWarnings.map((warning, index) => (
                  <div key={index} style={{ fontSize: 11, marginLeft: 8 }}>
                    • {warning}
                  </div>
                ))}
              </Alert>
            )}
          </div>
        ) : null }
      </div>
    </>
  );
}

const sheet = stylesheet({
  container: {
    padding: '0 15px 10px', 
    flexShrink: 0,
    width: percent(100),
    $nest: {},
  },
  textField: {
    $nest: {
      '& .MuiInputBase-root, fieldset': {
        color: ADALONG_COLORS.LIGHT_WHITE,
        borderColor: `#676666 !important`,
      },
    },
  },
});
